/**
 * Player-ability content rules — pure, import-free (plan 29 F2 + F8, lane BS).
 *
 * Shared by the compile gate (scripts/content/compile/playerAbilityContent.ts,
 * which runs in the compiler's minimal fixture and so may not pull the game's
 * runtime modules) and the runtime/test view (playerAbilities.ts,
 * abilityText.ts).
 */

/** The fields the rules read from an ability (camelCase, as generated). */
export interface PlayerAbilityRuleData {
  class?: string;
  abilityType?: string;
  target?: string;
  baseDamage?: number;
  tags?: string[];
  description?: string;
  vfx?: { hitWeight?: string };
}

const NON_DAMAGE_TYPES = new Set(['heal', 'defensive', 'buff']);

/**
 * Does this ability deal damage? Offensive abilities do; a defensive strike
 * aimed at an enemy (Shield of the Righteous) does; a utility or debuff that
 * authors a `base_damage` or swings in melee does too (Heroic Leap's slam,
 * Entangling Roots' bolt, Charge's strike on arrival). Heals, shields and
 * buffs never do.
 */
export function abilityDealsDamage(data: PlayerAbilityRuleData | undefined): boolean {
  if (!data) return false;
  if (data.abilityType === 'offensive') return true;
  if (data.abilityType === 'defensive' && data.target === 'enemy') return true;
  if (data.abilityType && NON_DAMAGE_TYPES.has(data.abilityType)) return false;
  return (data.baseDamage ?? 0) > 0 || (data.tags?.includes('melee') ?? false);
}

export type PlayerAbilitySource = 'curve' | 'job' | 'quest';

/** Curve classes that are not player classes (the internal dev kit). */
const NON_PLAYER_CURVES = new Set(['dev']);

/** ability id → how a player gets it: class curves, then jobs, then quest rewards. */
export function obtainableAbilities(input: {
  curves: Record<string, ReadonlyArray<{ abilityId: string }>>;
  jobAbilityIds: readonly string[];
  questRewardAbilityIds: readonly string[];
}): Map<string, PlayerAbilitySource> {
  const out = new Map<string, PlayerAbilitySource>();
  for (const [heroClass, unlocks] of Object.entries(input.curves)) {
    if (NON_PLAYER_CURVES.has(heroClass)) continue;
    for (const u of unlocks) if (!out.has(u.abilityId)) out.set(u.abilityId, 'curve');
  }
  for (const id of input.jobAbilityIds) if (!out.has(id)) out.set(id, 'job');
  for (const id of input.questRewardAbilityIds) if (!out.has(id)) out.set(id, 'quest');
  return out;
}

/** What each obtainable ability is missing; empty = it meets the content bar. */
export function playerAbilityProblems(
  obtainable: Iterable<string>,
  abilityData: Readonly<Record<string, PlayerAbilityRuleData | undefined>>,
): string[] {
  const problems: string[] = [];
  for (const id of [...obtainable].sort()) {
    const data = abilityData[id];
    if (!data) { problems.push(`${id}: obtainable but not defined in content/abilities`); continue; }
    if (!data.description?.trim()) problems.push(`${id}: no description (content/abilities/${data.class}.toml → description = "…")`);
    if (abilityDealsDamage(data) && !data.vfx?.hitWeight) {
      problems.push(`${id}: deals damage but authors no hit weight ([${id}.vfx] hit_weight = "tap|light|medium|heavy|epic")`);
    }
  }
  return problems;
}
